import { AddressAutofill } from '@mapbox/search-js-react';
import { useQuery } from '@apollo/client';
import { Form, Formik } from 'formik';
import { FormGroup, Label } from 'reactstrap';
import styled from 'styled-components';
import * as yup from 'yup';
import { useMemo } from 'react';
import {
  FARM_GROWING_ZONES,
  growingZoneSchema,
} from '../../constants/growingZones';
import { coordinatesSchema, coordsObjToPoint } from '../../utils/geojson';
import { useFarmCanBeEdited, useUser, useUserRole } from '../../utils/hooks';
import FormTypeahead from '../FormTypeahead';
import { ValidatedInput } from '../templates/template-components';
import OwnerSelector from './OwnerSelector';
import TeamSelector from './TeamSelector';
import { getFirstMutationResult } from '../../utils/graphql';
import { useIsMobile } from '../../utils/responsive';
import { captureEvent } from '../../utils/analytics';
import FarmFormButtonBlock from './FarmFormButtonBlock';
import { AllVisibleTagsQuery } from '../../queries';
import ClickToCopyButton from '../basics/ClickToCopyButton';
import useMutateWithState from '../../hooks/useMutateWithState';
import useGrowingZoneLookup from '../../hooks/useGrowingZoneLookup';
import { asSequentialAsync } from '../../utils/async';
import { alphanumericSort } from '../../utils/utils';
import MapCenterMinimap from './MapCenterMinimap';

// Automatically determine growing zone based on coordinates
// NOTE: we wrap this function in `asSequentialAsync` to prevent multiple concurrent executions of it
const setGrowingZoneFromCoords = asSequentialAsync(
  async ({ lookUpGrowingZone, setFieldValue, values }) => {
    const latVal = document.querySelector('input[name="mapCenter.lat"]')?.value;
    const lngVal = document.querySelector('input[name="mapCenter.lng"]')?.value;

    if (!latVal || !lngVal) return;
    const lat = parseFloat(latVal);
    const lng = parseFloat(lngVal);

    const growingZone = await lookUpGrowingZone({ lat, lng });

    if (growingZone === null) return; // unable to determine growing zone
    if (values.growingZone === growingZone) return; // matches existing value

    setFieldValue('growingZone', growingZone);
  },
);

const FieldOverlay = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  height: 30px;
  padding: 6px 10px;

  pointer-events: none;

  opacity: 0.75;
`;

const requiredStringSchema = yup.string().required('Required');

const farmSchema = yup.object().shape({
  name: requiredStringSchema,
  owner: yup
    .object()
    .shape({
      id: requiredStringSchema,
      email: requiredStringSchema,
    })
    .nullable()
    .default(null),
  team: yup
    .object()
    .shape({
      id: yup.string().nullable(),
    })
    .nullable()
    .default(null),
  startYear: yup.number().min(0, `Minimum is 0`).required('Required'),
  address: yup.string().nullable(),
  mapCenter: coordinatesSchema.required('Required'),
  growingZone: growingZoneSchema,
});

const FormContainer = styled.div`
  .form-group {
    margin-bottom: 0.5rem;
  }
`;

// Prevent a very long tag with no spaces from overflowing the form
const TagsFormGroup = styled(FormGroup)`
  min-width: 0;
`;

const isFarmOwner = (farm, user) => {
  const { owner } = farm;
  return owner && owner.id === user?.id;
};

const canEditFarmTeam = (farm) => {
  const { team } = farm;
  // This will return false for new team farms because
  // allowedActions isn't present, but we don't show the
  // team selector in that case anyway.
  return !team || team.allowedActions?.canEdit;
};

const TagsTypeahead = styled(FormTypeahead)`
  .rbt-input {
    overflow-y: scroll;
    height: calc(1.5em + 0.5rem + 2px);
  }
`;

const FarmForm = ({
  saveFarmMutation,
  showLabels = false,
  initialValues,
  submitButtonText,
  successMessage,
  onCancel,
  onCompleted,
  showTeamSelector = true,
  inModal = false,
}) => {
  // Fn to look up growing zone from USDA data (via tileset)
  const lookUpGrowingZone = useGrowingZoneLookup();

  const farmId = initialValues?.id;
  const isMobile = useIsMobile();
  const { isSuperadmin } = useUserRole();
  const { data: tagsData } = useQuery(AllVisibleTagsQuery);
  const [{ loading }, mutateAndSetState] = useMutateWithState();
  const user = useUser();
  const isOwner = isFarmOwner(initialValues, user);
  const canEditTeam = canEditFarmTeam(initialValues);
  const farmCanBeEdited = useFarmCanBeEdited(farmId);
  const canEditBasicSettings =
    (!farmId || isSuperadmin || farmCanBeEdited) && !initialValues.isLocked;
  const canEditAdvancedSettings =
    canEditBasicSettings && !initialValues.isArchived;
  const addressAutofillTheme = inModal
    ? {
        // Set a z-index on the results so that it can be used in a modal.
        cssText:
          '[class$="--Results"], [class*="--Results "] { z-index: 1060; }',
      }
    : undefined;

  const sortedTags = useMemo(() => {
    const tags = tagsData?.allVisibleTags || [];
    return [...tags].sort(alphanumericSort);
  }, [tagsData]);

  return (
    <FormContainer>
      <Formik
        initialValues={initialValues}
        enableReinitialize
        validationSchema={farmSchema}
        onSubmit={async (values, { setSubmitting }) => {
          const watchedFields = ['tags'];
          watchedFields.forEach((field) => {
            if (initialValues?.[field] !== values[field]) {
              captureEvent(`farm-${field}-updated`, {
                location: 'project settings',
              });
            }
          });
          try {
            const castValues = farmSchema.cast(values);
            const mapCenter = coordsObjToPoint(castValues.mapCenter);
            if (!mapCenter) throw new Error('Invalid map center coordinates');
            const response = await mutateAndSetState(
              () =>
                saveFarmMutation({
                  variables: {
                    input: {
                      ...castValues,
                      mapCenter,
                      ownerId: castValues.owner?.id,
                      owner: undefined,
                      teamId: castValues.team?.id,
                      team: undefined,
                      // New custom options will be an object where the label is the value
                      tags: (castValues.tags || []).map((tag) =>
                        typeof tag === 'string' ? tag : tag.label,
                      ),
                      hasValidSubscription: undefined,
                      isArchived: undefined,
                      isLocked: undefined,
                      __typename: undefined,
                    },
                  },
                }),
              successMessage,
            );
            // Errors are pulled from mutationWithState
            const result = getFirstMutationResult(response?.data);
            if (result && onCompleted) onCompleted();
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({ setFieldValue, setValues, values, touched }) => {
          const centerFeature = coordsObjToPoint(values.mapCenter);

          const updateGrowingZone = async () => {
            // Don't overwrite if the user has already manually entered a growing zone
            if (touched.growingZone) return;

            // Don't overwrite if the form already had the value set (i.e. editing an existing farm's settings)
            if (initialValues.growingZone) return;

            await setGrowingZoneFromCoords({
              lookUpGrowingZone,
              setFieldValue,
              values,
            });
          };

          // NOTE: this is only set to true in the moment that it happens (New Farm) -- this doesn't reflect whether or not a previously created Farm's growing zone was auto-generated
          const growingZoneWasAutoSet =
            !!values.growingZone && // growing zone is set
            !initialValues.growingZone && // growing zone was not initially set (i.e. editing an existing farm's settings)
            !touched.growingZone; // growing zone was not set manually

          return (
            <Form>
              <div className="d-flex">
                <ValidatedInput
                  type="text"
                  name="name"
                  label={showLabels && 'Farm Name'}
                  placeholder="Project/Farm Name"
                  formGroupClassName="mr-2 flex-grow-1"
                  disabled={!canEditBasicSettings}
                />
                {showTeamSelector && (isSuperadmin || canEditTeam) && (
                  <TeamSelector
                    showLabels={showLabels}
                    disabled={!canEditBasicSettings}
                  />
                )}
              </div>
              {farmId && (isSuperadmin || isOwner) && (
                <OwnerSelector
                  showLabels={showLabels}
                  farmId={farmId}
                  disabled={!canEditBasicSettings}
                />
              )}
              <>
                <div className={isMobile ? '' : 'd-flex'}>
                  <ValidatedInput
                    type="number"
                    name="startYear"
                    label={showLabels && 'Start Year'}
                    placeholder="Start Year"
                    formGroupClassName="mr-2 flex-shrink-0"
                    $showSpinner
                    disabled={!canEditAdvancedSettings}
                  />
                  <TagsFormGroup className="mr-2 flex-grow-1">
                    {showLabels && <Label>Tags</Label>}
                    <TagsTypeahead
                      options={sortedTags}
                      id="tags"
                      name="tags"
                      size="sm"
                      allowNew
                      newSelectionPrefix="Add tag: "
                      placeholder={
                        canEditAdvancedSettings ? 'Tags...' : undefined
                      }
                      emptyLabel="No tags found"
                      multiple
                      disabled={!canEditAdvancedSettings}
                    />
                  </TagsFormGroup>
                </div>
                <div className="d-flex">
                  <div className="flex-grow-1">
                    <AddressAutofill
                      theme={addressAutofillTheme}
                      accessToken={process.env.REACT_APP_MAPBOX_API_KEY}
                      onRetrieve={({ features }) => {
                        if (!features[0]) return;
                        const { coordinates } = features[0].geometry;
                        // Delay setting the address until the next tick so that the
                        // address field isn't overwritten by the autofill.
                        setTimeout(async () => {
                          setValues({
                            ...values,
                            mapCenter: {
                              lng: coordinates[0],
                              lat: coordinates[1],
                            },
                            address: features[0].properties.place_name,
                          });

                          // now that map center is set, try to auto-fill growing zone
                          await updateGrowingZone();
                        });
                      }}
                    >
                      <ValidatedInput
                        type="text"
                        name="address"
                        label={showLabels && 'Address'}
                        placeholder={
                          canEditAdvancedSettings
                            ? 'Address (123 James St...)'
                            : undefined
                        }
                        autoComplete="street-address"
                        formGroupClassName="mr-2"
                        disabled={!canEditAdvancedSettings}
                      />
                    </AddressAutofill>
                  </div>
                  <div
                    className={`mr-2 align-self-start ${
                      showLabels ? 'pt-3 mt-3' : 'mt-2'
                    }`}
                  >
                    OR
                  </div>
                  <ValidatedInput
                    formGroupClassName="mr-2"
                    type="number"
                    step="any"
                    name="mapCenter.lat"
                    onBlur={updateGrowingZone}
                    label={showLabels && 'Latitude'}
                    placeholder="Latitude"
                    disabled={!canEditAdvancedSettings}
                  />
                  <ValidatedInput
                    type="number"
                    step="any"
                    name="mapCenter.lng"
                    onBlur={updateGrowingZone}
                    label={showLabels && 'Longitude'}
                    placeholder="Longitude"
                    disabled={!canEditAdvancedSettings}
                  />
                  {centerFeature && (
                    <ClickToCopyButton
                      className="mb-3 ml-2 align-self-end"
                      copyValue={`${values.mapCenter.lat}, ${values.mapCenter.lng}`}
                      hoverOnly
                      iconSize="15px"
                    />
                  )}
                </div>
                <MapCenterMinimap />
                <FormGroup className="position-relative">
                  {showLabels && <Label>Growing Zone</Label>}
                  <FormTypeahead
                    id="growing-zone"
                    name="growingZone"
                    options={FARM_GROWING_ZONES}
                    placeholder="Select a growing zone..."
                    size="sm"
                    disabled={!canEditAdvancedSettings}
                  />
                  {growingZoneWasAutoSet && (
                    <FieldOverlay>
                      Growing zone auto-selected based on location ✅
                    </FieldOverlay>
                  )}
                </FormGroup>
              </>
              <FarmFormButtonBlock
                farm={initialValues}
                onCancel={onCancel}
                userCanEditFarm={
                  canEditAdvancedSettings || canEditBasicSettings
                }
                mutateAndSetState={mutateAndSetState}
                loading={loading}
                inModal={inModal}
                submitButtonText={submitButtonText}
              />
            </Form>
          );
        }}
      </Formik>
    </FormContainer>
  );
};

export default FarmForm;
